import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "./NotificationMainPopup.css";
import { useNavigate } from "react-router-dom";
import {
  fetchAnnouncements,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsSeen,
  markNotificationSeen,
} from "../../../services/NotificationApi";
import { getNotificationIcon } from "./notificationIcons";
import { getNotificationRedirectUrl } from "../../../utils/notificationRedirect";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/dates";

const NotificationPopup = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const popupRef = useRef(null);
  const [activeTab, setActiveTab] = useState("notifications");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [announcements, setAnnouncements] = useState([]);

  // Fetch notifications from API when popup opens
  useEffect(() => {
    if (!isOpen) return;

    const loadNotifications = async () => {
      setLoading(true);

      const notifData = await fetchNotifications();
      setNotifications(notifData);

      const annData = await fetchAnnouncements();
      setAnnouncements(annData);

      setLoading(false);
    };

    loadNotifications();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) loadUnread();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleViewAll = async () => {
    await markAllNotificationsSeen();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_seen: true })));
    setUnreadCount(0);

    onClose();

    if (activeTab === "notifications") {
      navigate("/notifications");
    } else {
      navigate("/notifications?tab=announcements");
    }
  };

  async function loadUnread() {
    const count = await fetchUnreadCount();
    setUnreadCount(count);
  }

  if (!isOpen) return null;

  const notificationList = notifications;
  const announcementList = announcements;

  const currentList =
    activeTab === "notifications" ? notificationList : announcementList;
  const isEmpty = currentList.length === 0;

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t("Now");
    if (diffMins < 60) return `${diffMins}m ${t("ago")}`;
    if (diffHours < 24) return `${diffHours}h ${t("ago")}`;
    if (diffDays === 1) return t("Yesterday");
    if (diffDays < 7) return `${diffDays} ${t("days ago")}`;
    return formatDate(dateStr);
  };

  const handleClick = async (item) => {
    if (activeTab !== "notifications") return;
    if (!item.is_seen) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, is_seen: true } : n)),
      );
      await markNotificationSeen(item.uuid);
    }

    const url = getNotificationRedirectUrl(item);
    if (url) navigate(url);
    onClose();
  };

  return (
    <div className="notifications-popup-overlay">
      <div className="notifications-popup-container" ref={popupRef}>
        <div className="notifications-popup">
          {/* Header Tabs */}
          <div className="blue-header">
            <div className="blue-header-left">
              <button
                className={` common-text ftw-5 notification-ma ${
                  activeTab === "notifications" ? "active-blue" : ""
                }`}
                onClick={() => setActiveTab("notifications")}
              >
                {t("Notifications")}
              </button>
              <button
                className={` common-text ftw-5 ${
                  activeTab === "announcements" ? "active-blue" : ""
                }`}
                onClick={() => setActiveTab("announcements")}
              >
                {t("Announcements")}
              </button>
            </div>
          </div>

          {/* Main content */}
          <div className="notifications-content">
            {loading ? (
              <div className="if-empty text-center">
                <span className="fts-14 ftw-6 common-text db">
                  {t("Loading...")}
                </span>
              </div>
            ) : isEmpty ? (
              <div className="if-empty text-center">
                <span className="fts-14 ftw-6 common-text db">
                  {activeTab === "notifications"
                    ? t("No Notifications Yet")
                    : t("No Announcements Yet")}
                </span>
                <div>
                  <span className=" ftw-4 common-text db">
                    {activeTab === "notifications"
                      ? t("You have no notifications right now.")
                      : t("You have no announcements right now.")}
                  </span>
                  <span className=" ftw-4 common-text db">
                    {t("Come back later.")}
                  </span>
                </div>
                <img
                  src="/assets/empty.png"
                  alt="emptylogo"
                  className="empty-image"
                />
              </div>
            ) : (
              currentList.map((item) => (
                <div
                  key={item.id}
                  onClick={
                    activeTab === "notifications"
                      ? () => handleClick(item)
                      : undefined
                  }
                  className={`notification-tiles-container ${
                    activeTab === "notifications" && !item.is_seen
                      ? "unread-bg-popup unread"
                      : ""
                  }`}
                  style={{
                    cursor:
                      activeTab === "notifications" ? "pointer" : "default",
                  }}
                >
                  <div className="notification-tile-left">
                    <div>
                      <img
                        src={
                          activeTab === "notifications"
                            ? getNotificationIcon(item.type)
                            : item.category?.icon
                        }
                        alt="icon"
                        className="popup-notification-icon"
                      />
                    </div>

                    <div className="not-pop-content">
                      <span className="d-block dk-gray fts-13 common-text  ftw-6 sentence-case">
                        {activeTab === "notifications"
                          ? item.title
                          : item.category?.name}
                      </span>

                      <span className="d-block dk-gray common-text fts-13">
                        {activeTab === "notifications"
                          ? item.message
                          : item.content?.replace(/<[^>]+>/g, "")}
                      </span>
                    </div>

                    <span className="lt-gray common-text fts-13">
                      {activeTab === "notifications"
                        ? formatTime(item.created_at)
                        : formatTime(item.created_time)}
                    </span>
                  </div>

                  {activeTab === "notifications" && !item.is_seen && (
                    <span className="unread-dot" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div
            className="view-all-announcement d-flex ai-center gap-6 cr-pointer"
            onClick={handleViewAll}
          >
            <span className="common-text blue fts-13 ftw-5">
              {activeTab === "notifications"
                ? t("View All Notifications")
                : t("View All Announcements")}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M2.91663 7H11.0833"
                stroke="#094C80"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 2.91663L11.0833 6.99996L7 11.0833"
                stroke="#094C80"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

NotificationPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NotificationPopup;
